/**
 * MX stock scarcity — keeps the scarcity block in sync on variant change.
 *
 * Mirrors Horizon's assets/product-inventory.js mechanism exactly: the
 * variant picker dispatches a ProductSelectEvent (with a promise resolving
 * to the section-rendered HTML) on the enclosing section, and each
 * interested component morphs itself from the matching element in that
 * new HTML.
 */

import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class MxStockScarcity extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles a product/variant select event by morphing in the re-rendered
   * scarcity markup.
   * @param {CustomEvent & { promise: Promise<{ detail: { html: Document, newProduct?: { id: number }, productId?: string } }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newScarcity = html.querySelector('mx-stock-scarcity');
        if (!newScarcity) return;

        morph(this, newScarcity, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mx-scarcity] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('mx-stock-scarcity')) {
  customElements.define('mx-stock-scarcity', MxStockScarcity);
}
